import React, { useState } from "react";
import { Check, Eye, Pencil, Trash2, X } from "lucide-react";
import MessageFiles from "./MessageFiles";
import { avatarStyle, firstNameOf, fullDate, initialsOf, isArabic } from "./format";

/** Thread: employee messages on the left, the signed-in admin's ("You") on the right. */
const Conversation = ({ replies, employeeName, me, canAct = false, busy = false, onEdit, onDelete }) => {
  const first = firstNameOf(employeeName);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const startEdit = (r) => { setConfirmId(null); setEditingId(r.id); setEditText(r.message); };
  const saveEdit = async (r) => {
    const text = editText.trim();
    if ((!text && !r.attachments?.length) || busy) return;
    if (text === r.message || (await onEdit(r.id, text))) setEditingId(null);
  };
  const remove = async (r) => {
    if (await onDelete(r.id)) setConfirmId(null);
  };
  return (
    <div className="er-section" style={{ gap: 12 }}>
      <div className="er-row" style={{ justifyContent: "space-between", gap: 8 }}>
        <div className="er-label">Conversation</div>
        <span className="er-row" style={{ gap: 5, fontSize: 11.5, color: "var(--faint)", whiteSpace: "nowrap" }}>
          <Eye size={12} /> Visible to {first}
        </span>
      </div>

      {replies.length === 0 && (
        <div style={{ padding: 18, borderRadius: 14, border: "1px dashed var(--line2)", textAlign: "center", fontSize: 13, color: "var(--faint)" }}>
          No replies yet. Your reply notifies {first} in the employee portal.
        </div>
      )}

      {replies.map((r) => {
        const fromEmployee = r.author === "employee";
        const mine = !fromEmployee && (!r.repliedBy || String(r.repliedBy) === String(me?.id));
        const who = fromEmployee ? employeeName : mine ? "You" : "Admin";
        const av = avatarStyle(fromEmployee ? employeeName : mine ? me?.name : "Admin");
        return (
          <div key={r.id} className={`er-msg${fromEmployee ? "" : " me"}`}>
            <div className="er-avatar" style={{ width: 26, height: 26, fontSize: 9.5, ...av }}>
              {initialsOf(fromEmployee ? employeeName : mine ? me?.name : "Admin")}
            </div>
            <div style={{ maxWidth: "78%", display: "flex", flexDirection: "column", gap: 4, alignItems: fromEmployee ? "flex-start" : "flex-end" }}>
              {editingId === r.id ? (
                <div className="er-editbox">
                  <div className="er-boxhead"><Pencil size={12} /> Editing message</div>
                  <textarea
                    dir="auto"
                    autoFocus
                    rows={3}
                    value={editText}
                    maxLength={2000}
                    onChange={(e) => setEditText(e.target.value)}
                    onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setEditingId(null);
                      else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); saveEdit(r); }
                    }}
                  />
                  <div className="er-row" style={{ gap: 8, justifyContent: "space-between" }}>
                    <span className="er-hint">Enter to save · Esc to cancel</span>
                    <span className="er-row" style={{ gap: 6 }}>
                      <button type="button" className="er-btn er-msgbtn" onClick={() => setEditingId(null)}>Cancel</button>
                      <button type="button" className="er-btn er-msgbtn primary" disabled={busy || (!editText.trim() && !r.attachments?.length)} onClick={() => saveEdit(r)}><Check size={13} strokeWidth={2.4} /> Save</button>
                    </span>
                  </div>
                </div>
              ) : confirmId === r.id ? (
                <div className="er-delcard" role="alertdialog" aria-label="Delete message">
                  <div className="er-boxhead danger"><Trash2 size={12} /> Delete this message?</div>
                  <div dir="auto" className="er-delpreview">{r.message || `📎 ${r.attachments?.length || 0} file(s)`}</div>
                  <div className="er-hint">It will also disappear for {first}. This can't be undone.</div>
                  <div className="er-row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    <button type="button" className="er-btn er-msgbtn" onClick={() => setConfirmId(null)}>Keep it</button>
                    <button type="button" className="er-btn er-msgbtn solid-danger" disabled={busy} onClick={() => remove(r)}><Trash2 size={13} /> Delete</button>
                  </div>
                </div>
              ) : (
                <div className="er-bwrap">
                  {canAct && mine && (
                    <div className="er-bar" role="toolbar" aria-label="Message actions">
                      <button type="button" className="er-btn er-baricon" title="Edit" aria-label="Edit message" onClick={() => startEdit(r)}><Pencil size={13} /></button>
                      <button type="button" className="er-btn er-baricon danger" title="Delete" aria-label="Delete message" onClick={() => { setEditingId(null); setConfirmId(r.id); }}><Trash2 size={13} /></button>
                    </div>
                  )}
                  <div className="er-bcol" style={{ alignItems: fromEmployee ? "flex-start" : "flex-end" }}>
                  {r.message && (
                  <div
                    dir="auto"
                    className={`er-bubble${isArabic(r.message) ? " ar" : ""}`}
                    style={
                      fromEmployee
                        ? { background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: "16px 16px 16px 4px" }
                        : { background: "var(--grad)", color: "var(--on-accent)", border: "1px solid transparent", borderRadius: "16px 16px 4px 16px" }
                    }
                  >
                    {r.message}
                  </div>
                  )}
                  <MessageFiles files={r.attachments} align={fromEmployee ? "flex-start" : "flex-end"} />
                  </div>
                </div>
              )}
              <span style={{ fontSize: 11, color: "var(--faint)" }}>{who} · {fullDate(r.createdAt)}{r.editedAt ? " · edited" : ""}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Conversation;
